/**
 * @fileoverview Body margins for a page-fit letterhead (MOR-1432)
 *
 * A page-fit header/footer keeps its artwork where the firm put it, so the
 * body column has to follow the firm's own letter too. The crop editor
 * measures that column from the letter text on the uploaded page; this row
 * shows the result and lets the user correct it or type their own values.
 * A blank input leaves that side unset, so it inherits the firm's margin
 * (and follows later changes to it); both blank saves nothing at all.
 */

import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  formatForUnit,
  parseInForUnit,
  useMarginUnit,
} from "@/utils/branding/marginUnits";
import type { SourceBodyMargins } from "@/utils/branding/extract-letterhead-images";
import type { DocumentPadding } from "@/utils/branding/resolve-letterhead";

type Side = "left" | "right";

export interface SourceBodyMarginsRowProps {
  /** The margins that will be saved (null = keep the firm's margins). */
  value: SourceBodyMargins | null;
  /** What the crop measured from the letter text (null = blank stationery). */
  detected: SourceBodyMargins | null;
  /** True once the user has typed their own values. */
  edited: boolean;
  /** The firm's margins, shown as placeholders and used for blank inputs. */
  inherited: DocumentPadding;
  onChange: (next: SourceBodyMargins | null) => void;
  onReset: () => void;
}

const SIDES: Array<{ side: Side; label: string }> = [
  { side: "left", label: "Left" },
  { side: "right", label: "Right" },
];

export function SourceBodyMarginsRow({
  value,
  detected,
  edited,
  inherited,
  onChange,
  onReset,
}: SourceBodyMarginsRowProps) {
  const [unit] = useMarginUnit();
  const draftFor = (side: Side): string => {
    const px = value?.[side];
    return px === undefined ? "" : formatForUnit(px, unit);
  };
  const [drafts, setDrafts] = useState<Record<Side, string>>({
    left: draftFor("left"),
    right: draftFor("right"),
  });
  useEffect(() => {
    setDrafts({ left: draftFor("left"), right: draftFor("right") });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draftFor only reads value + unit
  }, [value, unit]);

  const commit = (side: Side) => {
    const parsed = parseInForUnit(drafts[side], unit);
    if (parsed.kind === "invalid" || parsed.kind === "out_of_range") {
      toast.error(parsed.reason);
      setDrafts((d) => ({ ...d, [side]: draftFor(side) }));
      return;
    }
    const other: Side = side === "left" ? "right" : "left";
    // The other side keeps whatever is committed; only this side changes.
    const otherPx = value?.[other];
    const sidePx = parsed.kind === "ok" ? parsed.px : undefined;
    const leftPx = side === "left" ? sidePx : otherPx;
    const rightPx = side === "left" ? otherPx : sidePx;
    if (leftPx === undefined && rightPx === undefined) {
      if (value !== null) onChange(null);
      return;
    }
    if (value?.left === leftPx && value?.right === rightPx) return;
    onChange({
      ...(leftPx !== undefined ? { left: leftPx } : {}),
      ...(rightPx !== undefined ? { right: rightPx } : {}),
    });
  };

  const hint = (() => {
    if (detected && !edited) {
      return "Measured from the letter text on this page, so your documents start where the original letter's text does.";
    }
    if (!detected && !value) {
      return "No letter text on this page to measure. Enter the margins your letters use, or leave blank to keep the firm's margins.";
    }
    return "Your own values. A blank side keeps the firm's margin for that side.";
  })();

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border/60 bg-secondary/30 px-3 py-2"
      data-testid="source-body-margins"
    >
      <span className="text-xs font-medium text-primary/70">Body margins</span>
      {SIDES.map(({ side, label }) => (
        <label key={side} className="flex items-center gap-1.5 text-xs">
          <span className="text-primary/50">{label}</span>
          <input
            type="text"
            inputMode="decimal"
            aria-label={`Body margin ${label.toLowerCase()}`}
            className="h-7 w-16 rounded border border-border bg-background px-2 text-xs tabular-nums text-primary focus:outline-none focus:ring-1 focus:ring-primary/40"
            value={drafts[side]}
            placeholder={formatForUnit(
              side === "left" ? inherited.bodyLeft : inherited.bodyRight,
              unit,
            )}
            onChange={(e) =>
              setDrafts((d) => ({ ...d, [side]: e.target.value }))
            }
            onBlur={() => commit(side)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
          />
          <span className="text-primary/40">{unit}</span>
        </label>
      ))}
      {detected && edited && (
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 text-[11px] text-primary/50 transition hover:text-primary/80"
          title={`Back to the measured margins (${formatForUnit(detected.left ?? inherited.bodyLeft, unit)} / ${formatForUnit(detected.right ?? inherited.bodyRight, unit)} ${unit})`}
        >
          <RotateCcw className="size-3" />
          Use measured
        </button>
      )}
      <span className="basis-full text-[11px] text-primary/45">{hint}</span>
    </div>
  );
}
